import {
  DeviceGetReceiptsCallbackFunction,
  DeviceNotifyBarcodeScannedCallbackFunction,
  SDKAction,
  SDKGetAddonCallbackFunction,
} from '../types/actions';
import { Order, Customer } from '../order';

/** Environment to run the SDK. */
export enum Environment {
  /**
   * This option is used for testing the 3P add-on without running on Point of Sale app.
   * Any data returned by the POS are mocked. Data to be mocked can be defined in
   * {@link SdkInitializeOptions} during the SDK initialization.
   */
  LOCAL_HOST,

  /**
   * This option is used when running the application on Point of Sale. This is
   * the default if Environment is not set.
   */
  POS,
}

/**
 * Data retrieved from the Configuration API. This is one of the Contexts returned
 * during SDK initialization. See the
 * [Add-ons Configuration API Guide](https://docs.google.com/document/d/1RNxak9mWn3I6REY_HMTVKUsapdcJCQI_xc1hrer_cKo/edit#heading=h.yfkzcoa5vq50)
 * for more details on how to set data in ConfigurationsContext.
 */
type ConfigurationsContext = Record<string, boolean | string>;

/**
 * Context regarding the current POS device. This is one of the Context returned
 * during SDK initialization.
 */
type DeviceContext = {
  /** Whether the current device has a scanner connected. */
  hasScanner: boolean;

  /** ID of the device. */
  id?: string;
};

/**
 * Context regarding the current employee using the POS device. This is one of the
 * Context returned during SDK initialization.
 */
type EmployeeContext = {
  /** ID of the employee. */
  id?: string;
};

/** Encapsulates data required for reverse proxy. */
type ReverseProxyData = {
  /** Token to use when making requests to the reverse proxy. */
  token: string;

  /** URL of the reverse proxy. */
  url: string;
};

/** Context returned to 3P add-ons during SDK initialization. */
type Context = {
  /** Context regarding configurations set from Configuration API. */
  configurations: ConfigurationsContext;

  /** Context regarding current POS device. */
  device: DeviceContext;

  /** Context regarding current employee on POS. */
  employee: EmployeeContext;
};

/** Options that can be set on {@link SdkApi.initialize}. */
export interface SdkInitializeOptions {
  /** Environment to run the SDK on. Defaults to {@link Environment.POS}. */
  environment?: Environment;

  /**
   * Mocked data to be returned when environment is set to {@link Environment.LOCAL_HOST}.
   */
  localHostData?: {
    /** Mocked data of {@link Context}. */
    context?: Context;

    /** Mocked data of {@link Order}. */
    order?: Order;

    /** Mocked data of {@link Customer}. */
    customer?: Customer;
  };
}

/**
 * Set of callback functions that can be defined for each {@link SDKAction}.
 */
export interface SDKActionCallbacks {
  /** Set callback function for {@link SDKAction.SDK_GET_ADDON_INFO}. */
  [SDKAction.SDK_GET_ADDON_INFO]?: SDKGetAddonCallbackFunction;

  /** Set callback function for {@link SDKAction.DEVICE_NOTIFY_BARCODE_SCANNED}. */
  [SDKAction.DEVICE_NOTIFY_BARCODE_SCANNED]?: DeviceNotifyBarcodeScannedCallbackFunction;

  /** Set callback function for {@link SDKAction.DEVICE_GET_RECEIPTS}. */
  [SDKAction.DEVICE_GET_RECEIPTS]?: DeviceGetReceiptsCallbackFunction;
}

/** APIs directly related to the SDK. */
export interface SdkApi {
  /**
   * API to initialize the SDK. This method should be called prior to using any APIs
   * included in this library.
   * @param callbacks 3P add-ons can define which of the {@link SDKAction}s to register
   *   to and define how to handle these actions in {@link SDKActionCallbacks}.
   * @param options Additional options that can be set during SDK initialization.
   *   See {@link SdkInitializeOptions} for more info.
   * @returns Useful context object (such as {@link DeviceContext} which contains whether
   *   the Point of Sale device has scanner connected) that can be used by 3P add-ons.
   */
  initialize: (
    callbacks: SDKActionCallbacks,
    options?: SdkInitializeOptions
  ) => Promise<Context>;
}

export type {
  Context,
  ConfigurationsContext,
  DeviceContext,
  EmployeeContext,
  ReverseProxyData,
};
